import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from '../../components/admin/AdminLayout';
import axios from 'axios';
import { 
  Megaphone,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  X,
  AlertCircle,
  Calendar,
  Users,
  Download,
  Clock,
  RefreshCw,
  CheckCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status configuration
const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-slate-500/20 text-slate-400 border-slate-500/30' },
  active: { label: 'Active', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
  paused: { label: 'Paused', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
  completed: { label: 'Completed', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' }
};

// Create/Edit Campaign Modal
const CampaignModal = ({ isOpen, onClose, campaign, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    filters: '{}'
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (campaign) {
      setFormData({
        name: campaign.name,
        filters: JSON.stringify(campaign.filters, null, 2)
      });
    } else {
      setFormData({ name: '', filters: '{}' });
    }
    setError('');
  }, [campaign, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      // Validate JSON
      let parsedFilters;
      try {
        parsedFilters = JSON.parse(formData.filters);
      } catch {
        setError('Invalid JSON in filters field');
        setSaving(false);
        return;
      }

      const payload = {
        name: formData.name,
        filters: parsedFilters
      };

      if (campaign) {
        await axios.put(`${API_URL}/api/admin/campaigns/${campaign.id}`, payload);
      } else {
        await axios.post(`${API_URL}/api/admin/campaigns`, payload);
      }

      onSave();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save campaign');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-slate-800 border border-slate-700 rounded-xl w-full max-w-lg mx-4 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-700">
          <h2 className="text-xl font-semibold text-white">
            {campaign ? 'Edit Campaign' : 'Create Campaign'}
          </h2>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Campaign Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Q1 CEO Outreach"
              required
              className="w-full px-4 py-3 bg-slate-900/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-amber-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Filters (JSON)
            </label>
            <textarea
              value={formData.filters}
              onChange={(e) => setFormData({ ...formData, filters: e.target.value })}
              placeholder='{"titles": ["CEO", "CFO"], "countries": ["UAE"]}'
              rows={6}
              className="w-full px-4 py-3 bg-slate-900/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 font-mono text-sm"
            />
            <p className="text-xs text-slate-500 mt-1">
              Apollo search filters in JSON format
            </p>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-slate-400 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-slate-900 font-semibold rounded-lg hover:bg-amber-400 disabled:opacity-50 transition-colors"
            >
              {saving ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</>
              ) : (
                <>{campaign ? 'Update' : 'Create'} Campaign</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AdminCampaigns = () => {
  const { user } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [syncingId, setSyncingId] = useState(null);
  const [syncResult, setSyncResult] = useState(null);

  const fetchCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/campaigns`);
      setCampaigns(response.data);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  const handleCreate = () => {
    setEditingCampaign(null);
    setShowModal(true);
  };

  const handleEdit = (campaign) => {
    setEditingCampaign(campaign);
    setShowModal(true);
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) return;

    try {
      await axios.delete(`${API_URL}/api/admin/campaigns/${id}`);
      fetchCampaigns();
    } catch (error) {
      console.error('Error deleting campaign:', error);
    }
  };

  const handleSync = async (campaign) => {
    setSyncingId(campaign.id);
    setSyncResult(null);
    
    try {
      const response = await axios.post(`${API_URL}/api/admin/campaigns/${campaign.id}/sync`);
      setSyncResult({
        campaignId: campaign.id,
        success: response.data.success,
        message: response.data.message,
        newLeads: response.data.new_leads_imported,
        duplicates: response.data.duplicates_skipped
      });
      fetchCampaigns();
    } catch (error) {
      setSyncResult({
        campaignId: campaign.id,
        success: false,
        message: error.response?.data?.detail || 'Sync failed'
      });
    } finally {
      setSyncingId(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  return (
    <AdminLayout>
      {/* Modal */}
      <CampaignModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        campaign={editingCampaign}
        onSave={fetchCampaigns}
      />

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white mb-2" data-testid="campaigns-title">
            Campaigns
          </h1>
          <p className="text-slate-400">
            Manage lead generation campaigns
          </p>
        </div>
        <button
          onClick={handleCreate}
          data-testid="create-campaign-btn"
          className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-slate-900 font-semibold rounded-lg hover:bg-amber-400 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Create Campaign
        </button>
      </div>

      {/* Campaigns Table */}
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
          </div>
        ) : campaigns.length === 0 ? (
          <div className="text-center py-12">
            <Megaphone className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400">No campaigns yet</p>
            <p className="text-slate-500 text-sm mt-1">Create your first campaign to get started</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="text-left text-xs font-medium text-slate-400 uppercase tracking-wider px-6 py-4">
                    Name
                  </th>
                  <th className="text-left text-xs font-medium text-slate-400 uppercase tracking-wider px-6 py-4">
                    Status
                  </th>
                  <th className="text-left text-xs font-medium text-slate-400 uppercase tracking-wider px-6 py-4">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4" /> Total Results
                    </div>
                  </th>
                  <th className="text-left text-xs font-medium text-slate-400 uppercase tracking-wider px-6 py-4">
                    <div className="flex items-center gap-2">
                      <Download className="w-4 h-4" /> Imported
                    </div>
                  </th>
                  <th className="text-left text-xs font-medium text-slate-400 uppercase tracking-wider px-6 py-4">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" /> Last Sync
                    </div>
                  </th>
                  <th className="text-right text-xs font-medium text-slate-400 uppercase tracking-wider px-6 py-4">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                {campaigns.map((campaign) => (
                  <tr key={campaign.id} className="hover:bg-slate-700/20 transition-colors">
                    <td className="px-6 py-4">
                      <div>
                        <span className="font-medium text-white">{campaign.name}</span>
                        <p className="text-xs text-slate-500 mt-1">
                          Created: {formatDate(campaign.created_at)}
                        </p>
                        {/* Sync result message */}
                        {syncResult?.campaignId === campaign.id && (
                          <div className={`mt-2 flex items-center gap-1 text-xs ${syncResult.success ? 'text-emerald-400' : 'text-red-400'}`}>
                            {syncResult.success ? (
                              <>
                                <CheckCircle className="w-3 h-3" />
                                {syncResult.newLeads} imported, {syncResult.duplicates} skipped
                              </>
                            ) : (
                              <>
                                <AlertCircle className="w-3 h-3" />
                                {syncResult.message}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-block px-2 py-1 text-xs font-medium rounded border ${STATUS_CONFIG[campaign.status]?.color}`}>
                        {STATUS_CONFIG[campaign.status]?.label}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-white font-medium">{campaign.total_results.toLocaleString()}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-slate-300">{campaign.imported_count.toLocaleString()}</span>
                      {campaign.total_results > 0 && (
                        <span className="text-slate-500 text-xs ml-2">
                          ({Math.round((campaign.imported_count / campaign.total_results) * 100)}%)
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-slate-400 text-sm">{formatDate(campaign.last_sync_at)}</span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleSync(campaign)}
                          disabled={syncingId === campaign.id}
                          data-testid={`sync-campaign-${campaign.id}`}
                          className="flex items-center gap-1 px-3 py-1.5 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 disabled:opacity-50 transition-colors text-sm"
                        >
                          {syncingId === campaign.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <RefreshCw className="w-4 h-4" />
                          )}
                          Sync
                        </button>
                        <button
                          onClick={() => handleEdit(campaign)}
                          data-testid={`edit-campaign-${campaign.id}`}
                          className="flex items-center gap-1 px-3 py-1.5 bg-slate-700/50 text-slate-300 rounded-lg hover:bg-slate-700 transition-colors text-sm"
                        >
                          <Pencil className="w-4 h-4" />
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(campaign.id, campaign.name)}
                          data-testid={`delete-campaign-${campaign.id}`}
                          className="flex items-center gap-1 px-3 py-1.5 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors text-sm"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminCampaigns;
